import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useCart, CartItem } from '../contexts/CartContext';
import { formatPickupWindow } from '../utils/dateTimeUtils';
import { getVatBreakdown } from '../utils/vatUtils';
import { useTranslation } from '../hooks/useTranslation';

interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  address?: string;
}

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { cart } = useCart();
  const { t } = useTranslation();
  
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: '',
    email: '',
    phone: '',
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [cancellationPolicyAccepted, setCancellationPolicyAccepted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    // Redirect if cart is empty
    if (cart.length === 0) {
      navigate('/cart');
      return;
    }

    // Pre-fill information for authenticated users
    if (isSignedIn && user) {
      setCustomerInfo({
        name: user.fullName || '',
        email: user.primaryEmailAddress?.emailAddress || '',
        phone: user.primaryPhoneNumber?.phoneNumber || '',
      });
    }
  }, [isSignedIn, user, navigate, cart]);

  const handleInputChange = (field: keyof CustomerInfo, value: string) => {
    setCustomerInfo({ ...customerInfo, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!customerInfo.name.trim()) {
      newErrors.name = t('checkout.nameRequired');
    }

    if (!customerInfo.email.trim()) {
      newErrors.email = t('checkout.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
      newErrors.email = t('checkout.emailInvalid');
    }

    if (!customerInfo.phone.trim()) {
      newErrors.phone = t('checkout.phoneRequired');
    }

    if (!termsAccepted) {
      newErrors.terms = t('checkout.termsRequired');
    }

    if (!cancellationPolicyAccepted) {
      newErrors.cancellationPolicy = t('checkout.cancellationPolicyRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Store customer info in sessionStorage for payment page
    sessionStorage.setItem('customerInfo', JSON.stringify(customerInfo));
    sessionStorage.setItem('termsAccepted', JSON.stringify(termsAccepted));
    sessionStorage.setItem('cancellationPolicyAccepted', JSON.stringify(cancellationPolicyAccepted));

    navigate('/payment');
  };

  const calculateTotal = (): number => {
    return cart.reduce((total, item) => total + (item.quantity_kg * item.price_per_kg), 0);
  };

  // Group cart items by pickup date
  const groupedCart = cart.reduce((acc, item) => {
    const key = item.pickup_date_id;
    if (!acc[key]) {
      acc[key] = {
        pickup_date_id: item.pickup_date_id,
        pickup_date: item.pickup_date,
        pickup_start_time: item.pickup_start_time,
        pickup_end_time: item.pickup_end_time,
        location_name: item.location_name,
        location_address: item.location_address,
        items: [],
      };
    }
    acc[key].items.push(item);
    return acc;
  }, {} as Record<string, any>);

  if (cart.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>{t('checkout.cartEmpty')}</p>
      </div>
    );
  }

  const containerStyle: React.CSSProperties = {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '30px',
  };

  const mobileGridStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  };

  // Detect if screen is narrow (mobile)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const orderSummarySection = (
    <section style={{ order: isMobile ? 1 : 2 }}>
      <h2>{t('checkout.orderSummary')}</h2>
      
      {Object.values(groupedCart).map((group: any) => (
        <div key={group.pickup_date_id} style={{ 
          padding: isMobile ? '12px' : '20px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px', 
          marginBottom: isMobile ? '12px' : '20px' 
        }}>
          <h3 style={{ marginTop: 0, fontSize: isMobile ? '16px' : '18px' }}>{t('checkout.pickupDetails')}</h3>
          <p style={{ margin: '5px 0', fontSize: isMobile ? '14px' : '16px' }}>
            <strong>{t('checkout.dateTime')}:</strong><br />
            {formatPickupWindow(group.pickup_date, group.pickup_start_time, group.pickup_end_time, t('common.from'), t('common.to'))}
          </p>
          <p style={{ margin: '5px 0', fontSize: isMobile ? '14px' : '16px' }}>
            <strong>{t('checkout.location')}:</strong><br />
            {group.location_name}
          </p>
          <p style={{ margin: '5px 0', color: '#666', fontSize: isMobile ? '13px' : '16px' }}>
            {group.location_address}
          </p>

          <div style={{ marginTop: isMobile ? '10px' : '15px', paddingTop: isMobile ? '10px' : '15px', borderTop: '1px solid #ddd' }}>
            <h4 style={{ marginTop: 0, fontSize: isMobile ? '15px' : '16px' }}>{t('checkout.items')}</h4>
            {group.items.map((item: CartItem) => (
              <div key={`${item.product_id}-${item.pickup_date_id}`} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: isMobile ? '8px' : '10px', 
                paddingBottom: isMobile ? '8px' : '10px', 
                borderBottom: '1px solid #ddd' 
              }}>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: isMobile ? '14px' : '16px' }}>{item.product_name}</div>
                  <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#666' }}>
                    {item.quantity_kg} kg × €{item.price_per_kg.toFixed(2)}/kg
                  </div>
                </div>
                <div style={{ fontWeight: 'bold', fontSize: isMobile ? '14px' : '16px' }}>
                  €{(item.quantity_kg * item.price_per_kg).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ padding: isMobile ? '12px' : '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        {(() => {
          const total = calculateTotal();
          const vatBreakdown = getVatBreakdown(total);
          return (
            <>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '8px',
                fontSize: isMobile ? '14px' : '16px',
                color: '#666'
              }}>
                <span>{t('checkout.subtotalExclVat')}:</span>
                <span>€{vatBreakdown.priceExclVat.toFixed(2)}</span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '12px',
                fontSize: isMobile ? '14px' : '16px',
                color: '#666'
              }}>
                <span>{t('checkout.vat')}:</span>
                <span>€{vatBreakdown.vatAmount.toFixed(2)}</span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                fontSize: isMobile ? '18px' : '20px', 
                fontWeight: 'bold',
                paddingTop: '12px',
                borderTop: '2px solid #333'
              }}>
                <span>{t('checkout.totalInclVat')}:</span>
                <span>€{total.toFixed(2)}</span>
              </div>
            </>
          );
        })()}
      </div>
    </section>
  );

  const customerInfoSection = (
    <section style={{ order: isMobile ? 2 : 1 }}>
      <h2>{t('checkout.customerInformation')}</h2>
      <div>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            {t('checkout.name')} {t('checkout.required')}
          </label>
          <input
            type="text"
            value={customerInfo.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              border: errors.name ? '1px solid #dc3545' : '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px',
            }}
          />
          {errors.name && (
            <p style={{ color: '#dc3545', margin: '5px 0 0 0', fontSize: '14px' }}>
              {errors.name}
            </p>
          )}
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            {t('checkout.email')} {t('checkout.required')}
          </label>
          <input
            type="email"
            value={customerInfo.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              border: errors.email ? '1px solid #dc3545' : '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px',
            }}
          />
          {errors.email && (
            <p style={{ color: '#dc3545', margin: '5px 0 0 0', fontSize: '14px' }}>
              {errors.email}
            </p>
          )}
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            {t('checkout.phone')} {t('checkout.required')}
          </label>
          <input
            type="tel"
            value={customerInfo.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              border: errors.phone ? '1px solid #dc3545' : '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px',
            }}
          />
          {errors.phone && (
            <p style={{ color: '#dc3545', margin: '5px 0 0 0', fontSize: '14px' }}>
              {errors.phone}
            </p>
          )}
        </div>
      </div>
    </section>
  );

  const warningsSection = (
    <section style={{ order: 3, gridColumn: isMobile ? 'auto' : '1 / -1' }}>
      {/* Terms Checkbox */}
      <div style={{ 
        marginBottom: '15px', 
        padding: isMobile ? '12px' : '15px', 
        backgroundColor: '#fff3cd', 
        borderRadius: '4px', 
        border: errors.terms ? '1px solid #dc3545' : '1px solid #ffc107' 
      }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => {
              setTermsAccepted(e.target.checked);
              if (errors.terms) {
                const newErrors = { ...errors };
                delete newErrors.terms;
                setErrors(newErrors);
              }
            }}
            style={{ marginRight: '10px', marginTop: '3px', flexShrink: 0 }}
          />
          <span style={{ fontSize: isMobile ? '13px' : '14px' }}>
            {t('checkout.termsText')} {t('checkout.required')}
          </span>
        </label>
        {errors.terms && (
          <p style={{ color: '#dc3545', margin: '10px 0 0 0', fontSize: '14px' }}>
            {errors.terms}
          </p>
        )}
      </div>

      {/* Cancellation Policy Checkbox */}
      <div style={{ 
        marginBottom: '20px', 
        padding: isMobile ? '12px' : '15px', 
        backgroundColor: '#fff3cd', 
        borderRadius: '4px', 
        border: errors.cancellationPolicy ? '1px solid #dc3545' : '1px solid #ffc107' 
      }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={cancellationPolicyAccepted}
            onChange={(e) => {
              setCancellationPolicyAccepted(e.target.checked);
              if (errors.cancellationPolicy) {
                const newErrors = { ...errors };
                delete newErrors.cancellationPolicy;
                setErrors(newErrors);
              }
            }}
            style={{ marginRight: '10px', marginTop: '3px', flexShrink: 0 }}
          />
          <span style={{ fontSize: isMobile ? '13px' : '14px' }}>
            {t('checkout.cancellationPolicyText')} {t('checkout.required')}
          </span>
        </label>
        {errors.cancellationPolicy && (
          <p style={{ color: '#dc3545', margin: '10px 0 0 0', fontSize: '14px' }}>
            {errors.cancellationPolicy}
          </p>
        )}
      </div>

      {/* Submit Button */}
      <button
        type="button"
        onClick={handleSubmit}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: 'pointer',
        }}
      >
        {t('checkout.continueToPayment')}
      </button>
    </section>
  );

  return (
    <div style={containerStyle}>
      <h1>{t('checkout.title')}</h1>

      <div style={isMobile ? mobileGridStyle : gridStyle}>
        {orderSummarySection}
        {customerInfoSection}
        {warningsSection}
      </div>
    </div>
  );
};

export default CheckoutPage;
